import React from 'react';
import { CheckCircle2, Clock, Play, AlertCircle, Send, MoreHorizontal } from 'lucide-react';
import { ProgressUpdate, JobStatus } from '../../types/technicianJob';

interface ProgressTimelineProps {
  updates: ProgressUpdate[];
}

const STATUS_ICON: Record<JobStatus, React.ReactNode> = {
  assigned: <Clock className="w-3.5 h-3.5 text-sky-400" />,
  accepted: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />,
  in_progress: <Play className="w-3.5 h-3.5 text-amber-400" />,
  pending_inspection: <AlertCircle className="w-3.5 h-3.5 text-purple-400" />,
  completed: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />,
  rejected: <AlertCircle className="w-3.5 h-3.5 text-rose-400" />,
};

const STATUS_DOT: Record<JobStatus, string> = {
  assigned: 'bg-sky-400',
  accepted: 'bg-emerald-400',
  in_progress: 'bg-amber-400',
  pending_inspection: 'bg-purple-400',
  completed: 'bg-emerald-400',
  rejected: 'bg-rose-400',
};

function formatTime(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export const ProgressTimeline: React.FC<ProgressTimelineProps> = ({ updates }) => {
  if (updates.length === 0) {
    return <p className="text-xs text-slate-500 italic">No progress updates</p>;
  }

  const sorted = [...updates].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return (
    <div className="relative pl-4">
      <div className="absolute left-1.5 top-1 bottom-1 w-px bg-slate-700" />
      <div className="space-y-3">
        {sorted.map((update, i) => (
          <div key={update.id} className="relative flex gap-3">
            <div className={`absolute left-[-9px] top-1 w-3 h-3 rounded-full border-2 border-slate-800 ${STATUS_DOT[update.status]} flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {STATUS_ICON[update.status]}
                <span className="text-xs text-slate-200 font-medium">{update.message}</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">{formatTime(update.timestamp)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
